import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useLanguage from '../../hooks/useLanguage'
import { formatNumber } from '../../utils/formatters'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { CATEGORY_COLORS, getTheme } from './chartTheme'
import ChartTooltip from './ChartTooltip'

const CategoryPieChart = ({ data = [], height = 280, isDark = false }) => {
  const [activeIndex, setActiveIndex] = useState(null)
  const { t } = useTranslation('common')
  const { locale } = useLanguage()
  const theme = getTheme(isDark)

  const chartData = data.map((item, i) => ({
    name: item.category?.name || item.name || t('noCategory'),
    value: parseFloat(item.total || item.amount || 0),
    color: CATEGORY_COLORS[i % CATEGORY_COLORS.length],
  }))

  const total = chartData.reduce((sum, item) => sum + item.value, 0)

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height, color: 'var(--color-text-faint)' }}>
        {t('noData')}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            innerRadius="55%"
            outerRadius="80%"
            paddingAngle={3}
            dataKey="value"
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            stroke="none"
          >
            {chartData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                opacity={activeIndex === null || activeIndex === index ? 1 : 0.4}
                style={{ transition: 'opacity 0.2s ease' }}
              />
            ))}
          </Pie>
          <Tooltip content={<ChartTooltip />} />
          <text
            x="50%"
            y="48%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme.text}
            fontSize="20"
            fontWeight="700"
          >
            {formatNumber(total, locale)} {t('currency')}
          </text>
          <text
            x="50%"
            y="56%"
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme.textSecondary}
            fontSize="12"
          >
            {t('total')}
          </text>
        </PieChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-2 w-full max-w-xs">
        {chartData.map((entry, index) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: entry.color }}
            />
            <span className="truncate" style={{ color: 'var(--color-text-muted)' }}>{entry.name}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CategoryPieChart
